import React, { useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { Typography } from "@pondigitalsolutions/pon-component-library";

interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to call when modal should close */
  onClose: () => void;
  /** Modal content */
  children: React.ReactNode;
  /** Optional title for the modal */
  title?: string;
  /** Size variant for the modal */
  size?: "sm" | "md" | "lg";
  /** Custom className for the modal content */
  className?: string;
  /** Test ID for the modal */
  "data-testid"?: string;
}

const SIZE_VARIANTS = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
} as const;

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  size = "md",
  className,
  "data-testid": testId,
}) => {
  const modalRef = useRef<HTMLDialogElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  // Handle focus management
  useEffect(() => {
    if (isOpen) {
      // Store the previously focused element
      previouslyFocusedElement.current = document.activeElement as HTMLElement;

      // Focus the modal content
      if (modalRef.current) {
        modalRef.current.focus();
      }
    } else {
      // Restore focus to previously focused element
      if (previouslyFocusedElement.current) {
        previouslyFocusedElement.current.focus();
      }
    }
  }, [isOpen]);

  // Handle opening and closing the dialog
  useEffect(() => {
    if (modalRef.current) {
      if (isOpen) {
        modalRef.current.showModal();
      } else {
        modalRef.current.close();
      }
    }
  }, [isOpen]);

  // Handle ESC key and close events
  useEffect(() => {
    const handleClose = () => {
      onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation(); // Prevent event from bubbling up
        onClose();
      }
    };

    const handleCancel = (event: Event) => {
      // This handles backdrop clicks and ESC key presses
      event.preventDefault();
      event.stopPropagation(); // Prevent event from bubbling up
      onClose();
    };

    if (modalRef.current && isOpen) {
      const dialogElement = modalRef.current;

      dialogElement.addEventListener("close", handleClose);
      dialogElement.addEventListener("keydown", handleKeyDown);
      dialogElement.addEventListener("cancel", handleCancel);

      return () => {
        dialogElement.removeEventListener("close", handleClose);
        dialogElement.removeEventListener("keydown", handleKeyDown);
        dialogElement.removeEventListener("cancel", handleCancel);
      };
    }
  }, [isOpen, onClose]);

  return (
    <dialog
      ref={modalRef}
      className={twMerge(
        "rounded-xl bg-white shadow-xl",
        "dark:bg-tertiary-800 dark:text-tertiary-100",
        "animate-fade-in",
        "focus:outline-none",
        "backdrop:bg-secondary-900/50 backdrop:backdrop-blur-xs",
        "dark:backdrop:bg-secondary-950/70",
        "mx-auto my-auto",
        "p-0",
        SIZE_VARIANTS[size],
        className
      )}
      data-testid={testId}
      aria-labelledby={title ? "modal-title" : undefined}
      aria-modal="true"
    >
      {/* Header */}
      {title && (
        <div className="flex items-center justify-between p-6">
          {title && (
            <Typography
              id="modal-title"
              className="text-lg font-semibold text-tertiary-900 dark:text-tertiary-100"
            >
              {title}
            </Typography>
          )}
        </div>
      )}

      {/* Content */}
      <div className={twMerge("p-6", title && "pt-0")}>{children}</div>
    </dialog>
  );
};
